import * as Sentry from '@sentry/angular';

/**
 * Checks if the error is from a normal/expected WebSocket closure
 * Only filters closures that are expected user behavior, not actual connection problems
 */
function isNormalWebSocketClosure(hint: Sentry.EventHint): boolean {
  const originalException = hint?.originalException;
  if (originalException instanceof CloseEvent) {
    const code = originalException.code;

    // Filter only normal/expected close codes:
    // 1000 = Normal Closure - Clean disconnect
    // 1001 = Going Away - User navigated away from page
    // 1005 = No Status Received - Normal close without status (common in browsers)
    return code === 1000 || code === 1001 || code === 1005;
  }
  return false;
}

/**
 * Checks if the error is a View Transition API cancellation
 * These are expected browser behaviors when users switch tabs, resize windows, etc.
 */
function isViewTransitionCancellation(event: Sentry.ErrorEvent): boolean {
  const exceptionType = event.exception?.values?.[0]?.type;
  const exceptionValue = event.exception?.values?.[0]?.value || '';

  if (exceptionType === 'InvalidStateError' || exceptionType === 'AbortError') {
    return exceptionValue.toLowerCase().includes('view transition');
  }
  return false;
}

/**
 * Checks if the error is a network error from polyfills (user connection issue)
 * Application-level network errors are NOT filtered
 */
function isPolyfillNetworkError(event: Sentry.ErrorEvent): boolean {
  const exceptionType = event.exception?.values?.[0]?.type;
  const exceptionValue = event.exception?.values?.[0]?.value || '';

  if (exceptionType !== 'TypeError') {
    return false;
  }

  if (exceptionValue !== 'Failed to fetch' && exceptionValue !== 'Load failed') {
    return false;
  }

  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  const topFrame = frames?.[frames.length - 1];

  // Only filter if it's from polyfills or has no stack trace
  return !topFrame || topFrame.filename?.includes('polyfills') || false;
}

/**
 * Checks if the error originates from a browser extension
 */
function isBrowserExtensionError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;

  if (!frames || frames.length === 0) {
    return false;
  }

  return frames.some(frame => {
    const filename = frame.filename || '';
    return (
      filename.includes('chrome-extension://') ||
      filename.includes('moz-extension://') ||
      filename.includes('safari-extension://')
    );
  });
}

/**
 * Main error filter for Sentry beforeSend callback
 * Coordinates all filtering logic and returns null to drop events
 *
 * @param event - The Sentry error event
 * @param hint - Additional context about the error
 * @returns The event to send, or null to drop it
 */
export function filterSentryErrors(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  // Filter normal WebSocket closures
  if (isNormalWebSocketClosure(hint)) {
    return null;
  }

  // Filter View Transition API cancellations
  if (isViewTransitionCancellation(event)) {
    return null;
  }

  // // Filter network errors from polyfills
  // if (isPolyfillNetworkError(event)) {
  //   return null;
  // }
  //
  // // Filter browser extension errors
  // if (isBrowserExtensionError(event)) {
  //   return null;
  // }

  // Pass through all other errors
  return event;
}

/**
 * Sentry initialization options
 * Centralized configuration for error tracking
 */
export function getSentryConfig(dsn: string): Sentry.BrowserOptions {
  return {
    dsn,

    // ignoreErrors for simple pattern matching (most efficient)
    ignoreErrors: [
      // Module loading errors (happens after deploys when user didn't refresh the page, benign)
      'Failed to fetch dynamically imported module',
      '\'text/html\' is not a valid JavaScript MIME type',
      'Importing a module script failed',
      'error loading dynamically imported module',

      // View Transition API expected cancellations
      /view transition was skipped because document visibility state is hidden/i,
      /skipping view transition because viewport size changed/i,
      /skipping view transition because document visibility state has become hidden/i,
      /skipped ViewTransition due to document being hidden/i,
      /old view transition aborted by new view transition/i,

      // // ResizeObserver errors (benign browser quirk)
      // // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
      // 'ResizeObserver loop limit exceeded',
      //
      // // Third-party scripts (ad blockers, analytics)
      // 'fbq is not defined',
      // 'gtag is not defined',
      // 'ga is not defined',
      //
      // // Browser extension errors
      // /webkit\.messageHandlers/i,
    ],

    // beforeSend for complex filtering requiring event inspection
    beforeSend: filterSentryErrors,
  };
}
